import { z } from "zod";
import { env } from "./env";
import { getIdToken } from "./liff";

// ─────────────────────────────────────────
// 共通レスポンススキーマ
// ─────────────────────────────────────────

export const meSchema = z.object({
  lineUserId: z.string(),
  shopSlug: z.string(),
  shopName: z.string(),
});
export type Me = z.infer<typeof meSchema>;

export const shopInfoSchema = z.object({
  shop: z.object({
    slug: z.string(),
    name: z.string(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
  }),
  settings: z.object({
    businessHours: z.unknown(),
    closedDays: z.array(z.number()).nullable().optional(),
    seats: z.object({ counter: z.number(), zashiki: z.number() }),
    slotMinutes: z.number(),
    leadTimeMinHours: z.number(),
    leadTimeMaxDays: z.number(),
    maxPartyForOnline: z.number(),
    courses: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          priceYen: z.number(),
          minutes: z.number().optional(),
        }),
      )
      .nullable()
      .optional(),
  }),
});
export type ShopInfo = z.infer<typeof shopInfoSchema>;

export const availabilitySchema = z.object({
  slots: z.array(
    z.object({
      time: z.string(),
      sections: z.object({
        counter: z.boolean(),
        zashiki: z.boolean(),
        any: z.boolean(),
      }),
    }),
  ),
  partyTooLarge: z.boolean(),
  maxPartyForOnline: z.number().optional(),
});
export type Availability = z.infer<typeof availabilitySchema>;

export const reservationCreateResponseSchema = z.object({
  reservationId: z.string(),
  confirmedSection: z.enum(["counter", "zashiki"]),
  endAt: z.string(),
});
export type ReservationCreateResponse = z.infer<typeof reservationCreateResponseSchema>;

export const myReservationSchema = z.object({
  id: z.string(),
  reservedAt: z.string(),
  endAt: z.string().nullable(),
  partySize: z.number(),
  seatSection: z.string(),
  course: z.unknown().nullable(),
  status: z.string(),
  contactName: z.string().nullable(),
  notes: z.string().nullable(),
});
export type MyReservation = z.infer<typeof myReservationSchema>;

export const myReservationsSchema = z.object({
  reservations: z.array(myReservationSchema),
});

// ─────────────────────────────────────────
// fetch ラッパー
// ─────────────────────────────────────────

async function request<T>(
  prefix: "liff" | "admin",
  path: string,
  init: RequestInit,
  schema: z.ZodType<T>,
): Promise<T> {
  const token = getIdToken();
  const res = await fetch(`${env.apiBaseUrl}/api/${prefix}/${env.shopSlug}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail);
  }
  const json = await res.json();
  return schema.parse(json);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
  ) {
    super(`api_error_${status}`);
  }
}

// ─────────────────────────────────────────
// 個別 API
// ─────────────────────────────────────────

export const api = {
  me: () => request("liff", "/me", { method: "GET" }, meSchema),
  shop: () => request("liff", "/shop", { method: "GET" }, shopInfoSchema),
  availability: (params: { date: string; partySize: number; courseId?: string | null }) => {
    const qs = new URLSearchParams({
      date: params.date,
      partySize: String(params.partySize),
    });
    if (params.courseId) qs.set("courseId", params.courseId);
    return request("liff", `/availability?${qs.toString()}`, { method: "GET" }, availabilitySchema);
  },
  createReservation: (body: {
    reservedAt: string;
    partySize: number;
    seatSection: "counter" | "zashiki" | "any";
    courseId: string | null;
    contactName: string;
    contactPhone: string;
    notes?: string | null;
    idempotencyKey: string;
  }) =>
    request(
      "liff",
      "/reservation",
      { method: "POST", body: JSON.stringify(body) },
      reservationCreateResponseSchema,
    ),
  myReservations: () => request("liff", "/reservation/mine", { method: "GET" }, myReservationsSchema),
  cancelReservation: (id: string) =>
    request("liff", `/reservation/${id}`, { method: "DELETE" }, z.object({ ok: z.boolean() })),
};

// ─────────────────────────────────────────
// 管理 API（owner 専用）
// ─────────────────────────────────────────

export const adminMeSchema = z.object({
  lineUserId: z.string(),
  shopSlug: z.string(),
  shopName: z.string(),
  role: z.literal("owner"),
});

export const adminReservationSchema = z.object({
  id: z.string(),
  reservedAt: z.string(),
  endAt: z.string().nullable(),
  partySize: z.number(),
  seatSection: z.string(),
  course: z.string().nullable(),
  status: z.string(),
  source: z.string(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  notes: z.string().nullable(),
});
export type AdminReservation = z.infer<typeof adminReservationSchema>;

export const adminReservationsSchema = z.object({
  reservations: z.array(adminReservationSchema),
});

export const adminApi = {
  me: () => request("admin", "/me", { method: "GET" }, adminMeSchema),
  reservations: () =>
    request("admin", "/reservations", { method: "GET" }, adminReservationsSchema),
};
